import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type RenewalSummaryResult } from '@whinerp/client';
import { Button, EmptyState, ErrorState, PageHeading, Panel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';

/**
 * SC-79 Renewals (G-135).
 *
 * Two things this screen says out loud.
 *
 * The at-risk list comes first. Those are terms that end on their date unless
 * somebody wins them, and they are the reason a renewals manager opens the
 * screen at all; a stage summary above them would push them below the fold.
 *
 * The total is labelled as revenue already held, not bookings. Renewals are
 * kept out of the forecast and quota for exactly that reason, and a figure on
 * this screen that looked like pipeline would invite somebody to add it to
 * the new-business number.
 *
 * The deals themselves are moved on the ordinary board: winning or losing one
 * there is what changes the subscription, so there is one way to do it.
 */

function RenewalsScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const { can } = useSession();

  const summary = useQuery<RenewalSummaryResult, ApiError>({
    queryKey: ['renewal-summary'],
    queryFn: () => api.renewalSummary({}),
  });

  const openNow = useMutation({
    mutationFn: () => api.openRenewals({ body: {} }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['renewal-summary'] }),
  });

  if (summary.isPending) return <Loading />;
  if (summary.isError) {
    return (
      <div data-component="Renewals">
        <ErrorState title={t('state.error')} body={errorText(summary.error)} onRetry={() => void summary.refetch()} />
      </div>
    );
  }

  const data = summary.data.summary;

  return (
    <div data-component="Renewals" className="flex w-full flex-col gap-4">
      <PageHeading>{t('renewals.title')}</PageHeading>
      <p className="text-body text-ink-meta">{t('renewals.help')}</p>

      {can('revenue.manage') ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button intent="secondary" size="small" data-renewals-open onClick={() => openNow.mutate()} disabled={openNow.isPending}>
            {t('renewals.open')}
          </Button>
          {openNow.error ? <span className="text-dense text-danger-text">{errorText(openNow.error)}</span> : null}
          {openNow.data ? (
            <span className="text-dense text-ink-meta" data-renewals-opened>
              {t('renewals.opened', { values: { count: String(openNow.data.opened.length) } })}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* First, because these are why anybody opens this screen. */}
      <Panel data-renewals-at-risk>
        <h2 className="text-body text-ink">{t('renewals.atRisk')}</h2>
        <p className="text-meta text-ink-meta">{t('renewals.atRisk.help')}</p>
        {data.atRisk.length === 0 ? (
          <p className="mt-2 text-dense text-ink-meta" data-renewals-none-at-risk>
            {t('renewals.atRisk.none')}
          </p>
        ) : (
          <ul className="mt-2 flex flex-col gap-1 text-dense">
            {data.atRisk.map((deal) => (
              <li key={deal.dealId} data-renewal-at-risk={deal.dealId} className="flex flex-wrap items-baseline gap-2">
                <Link to={`/deals/${deal.dealId}`} className="text-ink underline-offset-2 hover:underline">
                  {deal.name}
                </Link>
                <span className="text-ink-600">
                  {t('renewals.daysLeft', { values: { days: String(deal.daysLeft) } })}
                </span>
                <span className="text-meta text-ink-meta">
                  {deal.amount} {deal.currency} · {deal.stage}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {data.open === 0 ? (
        <EmptyState title={t('renewals.empty')} body={t('renewals.empty.help')} />
      ) : (
        <Panel data-renewals-stages>
          {/* Labelled as held, not booked: renewals are kept out of the
              forecast so this figure is never added to new business. */}
          <p className="text-dense text-ink-600" data-renewals-total>
            {t('renewals.total', { values: { amount: data.openAmount, currency: data.currency, count: String(data.open) } })}
          </p>
          <ol className="mt-2 flex flex-col gap-1 text-dense">
            {data.byStage.map((stage) => (
              <li key={stage.stageKey} data-renewal-stage={stage.stageKey} className="flex justify-between gap-3">
                <span className="text-ink">{stage.label}</span>
                <span className="text-ink-meta">
                  {stage.deals} · {stage.amount} {data.currency}
                </span>
              </li>
            ))}
          </ol>
          {data.otherCurrency > 0 ? (
            <p className="mt-2 text-meta text-ink-meta">
              {t('renewals.otherCurrency', { values: { count: String(data.otherCurrency) } })}
            </p>
          ) : null}
        </Panel>
      )}
    </div>
  );
}

function Loading(): JSX.Element {
  return (
    <div data-component="Renewals">
      <Skeleton className="h-plot w-full" />
    </div>
  );
}

function Empty(): JSX.Element {
  const t = useT();
  return (
    <div data-component="Renewals">
      <EmptyState title={t('renewals.empty')} body={t('renewals.empty.help')} />
    </div>
  );
}

function Failed(): JSX.Element {
  const t = useT();
  return (
    <div data-component="Renewals">
      <ErrorState title={t('state.error')} body={t('renewals.help')} />
    </div>
  );
}

export const screen = defineScreen({
  id: 'SC-79',
  route: '/revenue/renewals',
  surface: 'page',
  title: 'Renewals',
  features: ['F-080'],
  entitlement: 'F-080',
  reachedBy: [{ kind: 'screen', from: 'SC-45', how: 'Open renewals from the revenue screen' }],
  components: [],
  elements: ['Renewals'],
  states: { empty: Empty, loading: Loading, error: Failed },
  component: RenewalsScreen,
});

export default screen;
